// Lib
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getCloneDescription } from '../cloneUtils';

// Selectors
import { cloneReachableInstanceCountSelector, cloneInstancesModeSelector } from '../cloneInstancesSelector';

// Actions
import { fetchCloneInstances } from '../cloneInstancesActions';

// Components
import Spinner from '../../../components/loaders/Spinner';
import ElementCloneInstancesPopupContentInfo from './ElementCloneInstancesPopupContentInfo';
import ElementCloneInstancesPopupContentMultiBoards from './ElementCloneInstancesPopupContentMultiBoards';
import { useTranslator } from '../../../components/i18n/useTranslator';

// Constants
import { CLONE_INSTANCES_UI_MODES } from '../cloneInstancesConstants';

// Types
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

// Styles
import './ElementCloneInstancesPopupContent.scss';

const ElementCloneInstancesPopupContent = (props: { elementId: string; element: ImMNElement }) => {
    const { elementId, element } = props;
    const translate = useTranslator('elements', 'clone');

    const dispatch = useDispatch();
    const mode = useSelector((state) => cloneInstancesModeSelector(state, props));
    const reachableInstanceCount = useSelector((state) => cloneReachableInstanceCountSelector(state, props));

    // When the popup opens, refresh its instances
    React.useEffect(() => {
        dispatch(fetchCloneInstances({ elementId, checkCloneProps: true, force: false }));
    }, []);

    switch (mode) {
        case CLONE_INSTANCES_UI_MODES.LOADING:
            return (
                <div className="ElementCloneInstancesPopupContentLoading">
                    <Spinner show />
                </div>
            );
        case CLONE_INSTANCES_UI_MODES.ERROR:
            return (
                <ElementCloneInstancesPopupContentInfo className="error">
                    {translate('instances.error', 'Something went wrong while fetching synced cards')}
                </ElementCloneInstancesPopupContentInfo>
            );
        case CLONE_INSTANCES_UI_MODES.NO_CLONES:
            return (
                <ElementCloneInstancesPopupContentInfo>
                    {translate('instances.removed', 'All other synced cards have now been removed')}
                </ElementCloneInstancesPopupContentInfo>
            );
        case CLONE_INSTANCES_UI_MODES.CURRENT_BOARD_ONLY:
            return (
                <ElementCloneInstancesPopupContentInfo className="current-board-only">
                    {translate(
                        'instances.currentBoardOnly',
                        '{cloneDescription} on the current board',
                        {
                            cloneDescription: getCloneDescription(element, reachableInstanceCount),
                        },
                    )}
                </ElementCloneInstancesPopupContentInfo>
            );
        case CLONE_INSTANCES_UI_MODES.MANY_BOARDS:
            return (
                <ElementCloneInstancesPopupContentMultiBoards
                    {...props}
                    reachableInstanceCount={reachableInstanceCount}
                />
            );
        case CLONE_INSTANCES_UI_MODES.PRIVATE_BOARDS:
            return (
                <ElementCloneInstancesPopupContentInfo>
                    <div>{translate('instances.privateBoards', 'All other synced copies are on private boards')}</div>
                </ElementCloneInstancesPopupContentInfo>
            );
        default:
            return (
                <ElementCloneInstancesPopupContentInfo className="error">
                    {translate('instances.invalidState', 'Invalid state')}
                </ElementCloneInstancesPopupContentInfo>
            );
    }
};

export default ElementCloneInstancesPopupContent;
